import type { UseCase } from 'api/types';
import type { ServerMessage } from 'entities/chat/api/chatClient';
import type { ClientType } from 'entities/onboarding/types';
import { VERTICAL_OPTIONS } from 'entities/onboarding/types';

export type Option = { id: string; title: string; description?: string };

export type SettledStatus = 'complete' | 'skipped';

// Enough of a checklist step to re-render it (faded) as a settled message, so the user can still
// open its link / rewatch its video.
export type SettledStepInfo = {
  title: string;
  description?: string | null;
  link?: string | null;
  videoUrl?: string | null;
};

export type OnStepSettled = (step: SettledStepInfo, status: SettledStatus) => void;

// A previewed order the assistant staged, awaiting the user's confirmation to actually place it.
export type OrderPreview = {
  previewToken: string;
  env: string;
  draft: Record<string, unknown>;
  threadId: string | null;
};

export type Message =
  | { id: string; role: 'system' | 'ai' | 'user'; kind: 'text'; text: string; streaming?: boolean }
  | { id: string; role: 'system'; kind: 'multi'; prompt: string; options: Option[] }
  | { id: string; role: 'system'; kind: 'checklist' }
  | { id: string; role: 'system'; kind: 'settled_step'; step: SettledStepInfo; status: SettledStatus }
  | {
      id: string;
      role: 'system';
      kind: 'order_preview';
      preview: OrderPreview;
      idempotencyKey: string;
    };

// The wizard runs vertical -> submitting -> checklist (client type is derived, not asked).
// `checklist` is where the server-driven checklist takes over.
export type Phase = 'vertical' | 'submitting' | 'checklist';

export type ConversationState = {
  phase: Phase;
  messages: Message[];
  clientType: ClientType | null;
  verticals: string[];
  pending: string[];
};

export type ConversationAction =
  | { type: 'TOGGLE_VERTICAL'; id: string }
  | { type: 'CONFIRM_VERTICALS' }
  | { type: 'SUBMIT_DONE' }
  | { type: 'SUBMIT_ERROR' }
  | { type: 'SETTLE_STEP'; step: SettledStepInfo; status: SettledStatus }
  | { type: 'SHOW_ORDER_PREVIEW'; preview: OrderPreview }
  | { type: 'LOAD_OLDER'; messages: Message[] }
  | { type: 'ASK_AI'; text: string }
  | { type: 'AI_DELTA'; text: string }
  | { type: 'AI_DONE' }
  | { type: 'AI_ERROR'; message: string };

const mkId = (): string => crypto.randomUUID();

const VERTICAL_CARDS: Option[] = VERTICAL_OPTIONS.map((o) => ({
  id: o.value,
  title: o.label,
  description: o.description,
}));

const VERTICAL_LABELS = new Map(VERTICAL_OPTIONS.map((o) => [o.value as string, o.label]));

function systemText(text: string): Message {
  return { id: mkId(), role: 'system', kind: 'text', text };
}

function userText(text: string): Message {
  return { id: mkId(), role: 'user', kind: 'text', text };
}

function verticalPrompt(): Message {
  return {
    id: mkId(),
    role: 'system',
    kind: 'multi',
    prompt: 'Which verticals are you working in? Select all that apply.',
    options: VERTICAL_CARDS,
  };
}

// The checklist is a message in the thread (not pinned to the bottom), so anything the user asks
// afterwards appends below it. A fresh conversation on reopen puts it back at the bottom.
function checklistMessage(): Message {
  return { id: mkId(), role: 'system', kind: 'checklist' };
}

function settledStepMessage(step: SettledStepInfo, status: SettledStatus): Message {
  return { id: mkId(), role: 'system', kind: 'settled_step', step, status };
}

// The streaming assistant message isn't necessarily last (e.g. SETTLE_STEP moves the checklist below
// it), so locate it by its streaming flag rather than by position.
function streamingIndex(messages: Message[]): number {
  return messages.findIndex(
    (message) => message.kind === 'text' && message.role === 'ai' && message.streaming === true,
  );
}

// Persisted assistant/user turns -> chat bubbles. Tool-only or empty rows have nothing to show, so
// they're dropped (their real ids still page correctly server-side via the raw response).
export function historyToMessages(serverMessages: ServerMessage[]): Message[] {
  const messages: Message[] = [];
  for (const message of serverMessages) {
    const text = message.content || (message.order_group_id ? 'Order placed.' : '');
    if (!text) {
      continue;
    }
    messages.push({ id: message.id, role: message.role === 'assistant' ? 'ai' : 'user', kind: 'text', text });
  }
  return messages;
}

// What we already know about the user from signup / the post-signup subscription wizard.
// `verticals: null` means the vertical (use_case) was never captured, so the wizard should ask it;
// an array (possibly empty) means it's known and the question is skipped.
export type Prefill = {
  clientType: ClientType;
  verticals: UseCase[] | null;
};

export function initialConversation(
  wizardComplete: boolean,
  prefill: Prefill,
  history: Message[] = [],
): ConversationState {
  if (wizardComplete) {
    // Prior conversation loads above; the checklist sits at the bottom on open. Skip the greeting
    // when there's history to show — the thread speaks for itself.
    const intro = history.length ? [] : [systemText("Welcome back! Here's your setup checklist.")];
    return {
      phase: 'checklist',
      messages: [...history, ...intro, checklistMessage()],
      clientType: null,
      verticals: [],
      pending: [],
    };
  }
  // Everything we ask for is already known -> submit silently and go straight to the checklist.
  if (prefill.verticals !== null) {
    return {
      phase: 'submitting',
      messages: [...history, systemText('Getting your setup ready…')],
      clientType: prefill.clientType,
      verticals: prefill.verticals,
      pending: prefill.verticals,
    };
  }
  // Account type is known; only the vertical is missing (e.g. the subscription wizard was skipped).
  return {
    phase: 'vertical',
    messages: [...history, systemText("Hi! Let's get you set up. One quick question."), verticalPrompt()],
    clientType: prefill.clientType,
    verticals: [],
    pending: [],
  };
}

export function conversationReducer(state: ConversationState, action: ConversationAction): ConversationState {
  switch (action.type) {
    case 'TOGGLE_VERTICAL': {
      const pending = state.pending.includes(action.id)
        ? state.pending.filter((id) => id !== action.id)
        : [...state.pending, action.id];
      return { ...state, pending };
    }

    case 'CONFIRM_VERTICALS': {
      const labels = state.pending.map((id) => VERTICAL_LABELS.get(id) ?? id);
      return {
        ...state,
        verticals: state.pending,
        phase: 'submitting',
        messages: [...state.messages, userText(labels.length ? labels.join(', ') : 'Skip for now')],
      };
    }

    case 'SUBMIT_DONE':
      return {
        ...state,
        phase: 'checklist',
        messages: [...state.messages, checklistMessage()],
      };

    case 'SUBMIT_ERROR': {
      // If we submitted silently (no vertical question was ever shown), surface it now so the user
      // has a way to retry via CONFIRM_VERTICALS.
      const needsPrompt = !state.messages.some((message) => message.kind === 'multi');
      return {
        ...state,
        phase: 'vertical',
        messages: [
          ...state.messages,
          systemText("Something went wrong saving that. Let's try again."),
          ...(needsPrompt ? [verticalPrompt()] : []),
        ],
      };
    }

    case 'SETTLE_STEP': {
      // Record the just-acted step as its own message, and keep the checklist (which now shows the
      // next step) at the very bottom so the new step reads as appearing below the settled one.
      const settled = settledStepMessage(action.step, action.status);
      const checklist = state.messages.find((message) => message.kind === 'checklist');
      const rest = state.messages.filter((message) => message.kind !== 'checklist');
      return { ...state, messages: checklist ? [...rest, settled, checklist] : [...rest, settled] };
    }

    case 'SHOW_ORDER_PREVIEW': {
      // Skip a duplicate preview for the same token, and derive a stable idempotency key from it so a
      // replayed preview can't slip past confirmOrder's idempotency check with a fresh key.
      const alreadyShown = state.messages.some(
        (message) => message.kind === 'order_preview' && message.preview.previewToken === action.preview.previewToken,
      );
      if (alreadyShown) {
        return state;
      }
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: mkId(),
            role: 'system',
            kind: 'order_preview',
            preview: action.preview,
            idempotencyKey: action.preview.previewToken,
          },
        ],
      };
    }

    case 'LOAD_OLDER':
      return { ...state, messages: [...action.messages, ...state.messages] };

    case 'ASK_AI':
      return {
        ...state,
        messages: [
          ...state.messages,
          userText(action.text),
          { id: mkId(), role: 'ai', kind: 'text', text: '', streaming: true },
        ],
      };

    case 'AI_DELTA': {
      const idx = streamingIndex(state.messages);
      if (idx === -1) {
        return state;
      }
      const messages = [...state.messages];
      const msg = messages[idx];
      if (msg.kind === 'text') {
        messages[idx] = { ...msg, text: msg.text + action.text };
      }
      return { ...state, messages };
    }

    case 'AI_DONE': {
      const idx = streamingIndex(state.messages);
      if (idx === -1) {
        return state;
      }
      const messages = [...state.messages];
      const msg = messages[idx];
      if (msg.kind === 'text') {
        messages[idx] = { ...msg, streaming: false };
      }
      return { ...state, messages };
    }

    case 'AI_ERROR': {
      const idx = streamingIndex(state.messages);
      if (idx === -1) {
        return state;
      }
      const messages = [...state.messages];
      const msg = messages[idx];
      if (msg.kind === 'text') {
        messages[idx] = { ...msg, text: action.message, streaming: false };
      }
      return { ...state, messages };
    }

    default:
      return state;
  }
}
